// @flow

<template>
  <v-layout>
    <div class="col">
      <div class="head-page-title">
        <h1 class="main-title">Kaart vervangen</h1>
      </div>
      <div class="panel">
        <div class="row">
          <div class="small-12 columns">
            <div id="select-cards-text">Kies de te vervangen OV-chipkaart(en)</div>
            <div class="datatable-wrapper">
              <div class="dataTables_wrapper dt-foundation no-footer">
                <table class="datatable datatable-row-click dataTable no-footer" role="grid" style="width: 800px;">
                  <thead>
                    <table-header :order-by="orderBy"
                                  :order-direction="orderDirection"
                                  :columns="headerColumns"
                                  translation-prefix="orderReplacementCard.headerColumns."
                                  @orderingChanged="setOrderingAndLoadData"/>
                    <order-replacement-card-table-header-filter :filter-values="filter"
                                                                @filteringChanged="setFilteringAndLoadData"/>
                  </thead>
                  <tbody>
                    <tr is="order-replacement-card-list-item"
                        v-for="companyCard in resultsForCurrentFilter"
                        :card-summary="companyCard"
                        :key="companyCard.cardId"
                        :is-card-checked="Boolean(cardFormDataById(companyCard.cardId))"
                        @rowCheckChange="updateRowCheck"/>
                    <tr v-if="!resultsForCurrentFilter || resultsForCurrentFilter.length === 0">
                      <td :colspan="headerColumns.length">Geen resultaten</td>
                    </tr>
                  </tbody>
                </table>
                <div class="bottom">
                  <div class="dataTables_paginate paging_full_numbers">
                    <order-replacement-card-list-paginator :page-size="pageSize" @paginationChanged="loadData"/>
                  </div>
                </div>
                <div class="clear"/>
              </div>
            </div>
            <replacement-card-form/>
          </div>
        </div>
      </div>
    </div>
  </v-layout>
</template>

<script>
import _ from 'lodash'
import VLayout from '../layouts/Default'
import { mapState, mapMutations, mapActions, mapGetters } from 'vuex'
import OrderReplacementCardListPaginator from '../components/orderReplacementCard/OrderReplacementCardListPaginator'
import OrderReplacementCardListItem from '../components/orderReplacementCard/OrderReplacementCardListItem'
import OrderReplacementCardTableHeaderFilter from '../components/orderReplacementCard/OrderReplacementCardTableHeaderFilter'
import showAlertErrorHandler from '../mixins/show-alert-error-handler'
import TableHeader from '../components/tableHeader/TableHeader'
import {
  ADD_FORM_DATA_ITEM,
  DELETE_FORM_DATA_ITEM,
  SET_ORDER_BY,
  SET_ORDER_DIRECTION,
  SET_PAGE_SIZE
} from '../store/modules/order-replacement-card/mutation-types'
import {buildTableHeaderColumn} from '../types/table-header-column'
import ReplacementCardForm from '../components/forms/ReplacementCardForm'
import type {FilterPair} from '../types/filter-pair'
import {CARD_TYPE_PERSONAL} from '../types/card-type'
import type {CardDetails} from '../types/card-details'
import {dateFormatOrDefault} from '../filters/date-format'

export default {
  name: 'OrderReplacementCardPage',
  components: {
    ReplacementCardForm,
    TableHeader,
    OrderReplacementCardListPaginator,
    OrderReplacementCardListItem,
    OrderReplacementCardTableHeaderFilter,
    VLayout
  },
  mixins: [showAlertErrorHandler],
  data: () => {
    return {
      headerColumns: [
        buildTableHeaderColumn('multiSelect', '2.5%', null, false, false),
        buildTableHeaderColumn('engravedId', '25%'),
        buildTableHeaderColumn('cardHolderDetails'),
        buildTableHeaderColumn('combinedAscriptions'),
        buildTableHeaderColumn('validUntil', '15%')
      ]
    }
  },
  computed: {
    ...mapState('orderReplacementCard', [
      'resultsForCurrentFilter',
      'pageSize',
      'currentPageNumber',
      'orderBy',
      'orderDirection',
      'filter',
      'formData'
    ]),
    ...mapGetters('orderReplacementCard', [
      'cardFormDataById'
    ])
  },
  mounted () {
    this.loadData()
  },
  methods: {
    ...mapMutations('orderReplacementCard', {
      setPageSize: SET_PAGE_SIZE,
      setOrderBy: SET_ORDER_BY,
      setOrderDirection: SET_ORDER_DIRECTION,
      addFormDataItem: ADD_FORM_DATA_ITEM,
      deleteFormDataItem: DELETE_FORM_DATA_ITEM
    }),
    ...mapActions('orderReplacementCard', [
      'loadReplaceableCards',
      'loadCardDetails',
      'setFilterPairAndResetCurrentPageNumber'
    ]),
    loadData: async function () {
      try {
        await this.loadReplaceableCards()
      } catch (error) {
        this.showAlertForErrorAndRedirectToRoute(error, {}, {401: {name: 'login'}})
      }
    },
    setOrderingAndLoadData (ordering) {
      this.setOrderBy(ordering.orderBy)
      this.setOrderDirection(ordering.orderDirection)
      this.loadData()
    },
    setFilteringAndLoadData (filtering: FilterPair) {
      this.setFilterPairAndResetCurrentPageNumber(filtering)
      this.debouncedLoadData()
    },
    debouncedLoadData: _.debounce(
      function () {
        this.loadData()
      },
      250
    ),
    async updateRowCheck (data) {
      if (data.newIsCheckboxChecked) {
        this.createAndCommitInitialSubFormData(await this.loadCardDetails(data.cardId))
      } else {
        this.deleteFormDataItem(data.cardId)
      }
    },
    async loadCardDetails (cardId) {
      return this.$container.b2bClient.getCardDetails(cardId)
    },
    createAndCommitInitialSubFormData (cardDetails: CardDetails) {
      let subFormData = {
        cardId: cardDetails.cardId,
        ascription: cardDetails.ascription,
        ascription1: cardDetails.ascription1,
        ascription2: cardDetails.ascription2,
        cardTheme: cardDetails.cardTheme,
        cardType: cardDetails.cardType,
        engravedId: cardDetails.engravedId,
        defaultCompanyNameOnCard: cardDetails.contract ? cardDetails.contract.defaultCompanyNameOnCard : ''
      }

      if (cardDetails.cardType === CARD_TYPE_PERSONAL) {
        const customer = cardDetails.customer
        subFormData.cardHolderDetails = {
          gender: customer ? customer.gender : '',
          firstName: customer ? customer.firstName : '',
          surnamePrefix: customer ? customer.surnamePrefix : '',
          surname: customer ? customer.surname : '',
          dateOfBirth: dateFormatOrDefault(customer ? customer.dateOfBirth : null, '')
        }
      }

      this.addFormDataItem(subFormData)
    }
  }
}
</script>

<style>
  #select-cards-text {
    margin-bottom: 20px;
  }

  table.dataTable thead th input {
    height: 30px;
    margin-bottom: 0;
  }

  table.dataTable thead th {
    padding-top: 5px;
  }
</style>



// WEBPACK FOOTER //
// src/views/OrderReplacementCardPage.vue